import { AxiosError } from 'axios';
import { useEffect, useState } from 'react';
import { createSearchParams, useNavigate } from 'react-router-dom';
import RecapBeneficiaryFormComponent from '../../components/recap/RecapBeneficiaryFormComponent';
import { APP_URLS } from '../../const/AppConst';
import useAppState from '../../context/AppContext';
import { IApiErrors, IUpdatedProposition } from '../../context/model';
import MgpMessage from '../../elements/MgpMessage/MgpMessage';
import ConfirmModal from '../../modals/common/ConfirmModal';
import LoaderModal from '../../modals/common/LoaderModal';
import { lockProposition } from '../../services/CustomerApiService';
import { displaySteps, getRequireFamilyDocumentTypes, isCompletedProposition, isDoneProposition, isPropositionInDraft, isPropositionInLock, isWaitingClient } from '../../utils/dataUtils';
import StickButtonsBloc from '../common/StickButtonsBloc';
import RecapGuarantyFormComponent from '../../components/recap/RecapGuarantyFormComponent';
import MgpButton from '../../elements/MgpButton/MgpButton';
import InfoPersoRecapFormComponent from '../../components/info-perso/InfoPersoRecapFormComponent';
import { getDayDifferent } from '../../utils/utils';

export default function RecapBloc() {
  const navigate = useNavigate();

  const [errorMsg, setErrorMsg] = useState<string | null>();
  const [loading, setLoading] = useState(false);
  const {
    state: { jwtToken, proposition, updatedProposition, signedDocument, isConseiller, additionalData, familyDoc },
    dispatch,
  } = useAppState();  
  
  const [isShowModal, setIsShowModal] = useState(false);

  const showConfirmationModal = () => {
    const requiredDocs = getRequireFamilyDocumentTypes(updatedProposition!, additionalData);

    if (familyDoc.length < requiredDocs.length) {
      setErrorMsg("Merci de joindre votre justificatif de situation familiale.")
    } else {
      setIsShowModal(true);
    }
  }

  const closeModal = () => {
    setIsShowModal(false)
  }

  const onNextAction = async () => {
    closeModal();
    setErrorMsg(null);
    try {
      if(updatedProposition && proposition) {
        let newProposition;
        if (isPropositionInDraft(proposition)) {
          setLoading(true);
      
          const payload = await lockProposition(jwtToken!.accessToken, proposition?.propositionId, updatedProposition);
          newProposition = payload.data;
          let steps = displaySteps(newProposition);
          dispatch({type: "UPDATE_STEPS", steps: steps});      
        }
        

        if(newProposition?.configuration.hasSignature) {
          dispatch({type: 'NEXT_INFO_STEP', path: APP_URLS.infoSignatureStep0Page, proposition: newProposition});
          navigate({
            pathname: APP_URLS.infoSignatureStep0Page,
            search: createSearchParams({
                id: newProposition?.propositionId
            }).toString(),
          }, {replace: true});
        } else {
          dispatch({type: 'NEXT_INFO_STEP', path: APP_URLS.endPage, proposition: newProposition});
          navigate({
            pathname: APP_URLS.endPage,
            search: createSearchParams({
                id: proposition?.propositionId
            }).toString(),
          }, {replace: true});
        }
      } 

    } catch (error) {
      const err = error as AxiosError;
      console.log(err);
      setLoading(false);
      if(err.response && err.response.status == 400) {
        const errorData = err.response?.data as IApiErrors;
        setErrorMsg("Erreur: " + errorData.message);
      } else {
        setErrorMsg("Une erreur technique est survenue ");
      }
    }
  }

  const onNextLockedAction =  () => {
    closeModal();
    setErrorMsg(null);
    let nextUrl = APP_URLS.endPage;
    let replaceUrl = false;
    let hasSignature = proposition?.configuration.hasSignature;
    if(isDoneProposition(proposition!)) {
      if(hasSignature && signedDocument.length > 0) {
        nextUrl = APP_URLS.infoSignatureStep2Page;
      } else {
        nextUrl = APP_URLS.listInvalidDocumentPage;
      }
    } else if (isWaitingClient(proposition!)) {
      nextUrl = APP_URLS.listInvalidDocumentPage;
    } else if(isCompletedProposition(proposition!)) {
      if(hasSignature) { 
        if (signedDocument.length > 0) {
          nextUrl = APP_URLS.infoSignatureStep2Page;
          replaceUrl = true;
        } else {
          nextUrl = APP_URLS.infoSignatureStep0Page;
          replaceUrl = true;
        }
      }
    }
    dispatch({type: 'NEXT_INFO_STEP', path: nextUrl});
    
    navigate({
      pathname: nextUrl,
      search: createSearchParams({
          id: proposition!.propositionId
      }).toString(),
    }, {replace: replaceUrl});
  }

  const onPreviousAction = () => {
    if(isPropositionInLock(proposition!) && isConseiller) {
      dispatch({type: 'BACK_INFO_STEP', path: APP_URLS.postSignatureActionPage});
      navigate({
        pathname: APP_URLS.postSignatureActionPage,
        search: createSearchParams({
            id: proposition!.propositionId
        }).toString(),
      });
      return;
    }
    
    let backUrl = APP_URLS.infoPersoStep6Page;
    if(proposition?.configuration.hasPreviousInsuranceTermination && getDayDifferent(proposition?.effectiveDate) > 32) {
      backUrl = APP_URLS.infoOldContractPage;
    } else if(proposition?.configuration.hasOfferStep) {
      backUrl = APP_URLS.guarantyPage;
    } else if(proposition?.configuration.canBeAddedBeneficiaries){
      if(additionalData?.hasChild || additionalData?.hasPartner) {
        backUrl = APP_URLS.familyDocumentsPage;
      } else {
        backUrl = APP_URLS.infoBeneficiaryStep0Page;
      }
    }

    dispatch({type: 'BACK_INFO_STEP', path: backUrl});
    navigate({
      pathname: backUrl,
      search: createSearchParams({
          id: proposition!.propositionId
      }).toString(),
    });
  }

    const [isScrolledToBottom, setIsScrolledToBottom] = useState(false);
    const [isShowRecapInfoPerso, setIsShowRecapInfoPerso] = useState(false);

    const handleScroll = () => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const windowHeight = window.innerHeight;
      const docHeight = document.documentElement.scrollHeight;
      const scrollPercent = scrollTop / (docHeight - windowHeight) * 100;
      setIsScrolledToBottom(scrollPercent === 100);
    };
    
    const handleButtonClick = () => {
      if (isScrolledToBottom) {
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        window.scrollTo({ top: document.documentElement.scrollHeight, behavior: "smooth" });
      }
    };
    
    useEffect(() => {
      window.addEventListener("scroll", handleScroll);
      return () => window.removeEventListener("scroll", handleScroll);
    }, []);
    
    const onShowRecapInfoPerso = (e: any) => {
      e.preventDefault();
      setIsShowRecapInfoPerso(!isShowRecapInfoPerso);
    }
  
    return (
      
      <div id='recap-bloc'>
        <div>
          {errorMsg && <MgpMessage type="danger" message={errorMsg} hasCloseBtn={true}/>}
        </div>
        {((updatedProposition!.hasOldMutual && updatedProposition?.oldCompetitor?.isOtherName === false) || updatedProposition?.hasMgpInsurance || (proposition?.oldCompetitor && Object.keys(proposition?.oldCompetitor)?.length !== 0 && proposition?.oldCompetitor?.isOtherName === false) || proposition?.hasMgpInsurance) && 
          <div className='flex-grid bg-blanc shadow-noir p-md mt-lg m-lg-desktop'>
            <MgpMessage 
              type='success'
              message="La MGP effectuera les démarches de résiliation auprès de votre ancien organisme et s'assurera de la continuité de votre couverture."
            />
          </div>
        }

        {
          proposition?.configuration.hasOfferStep && updatedProposition!.offerCode && updatedProposition!.offerCode.length > 0 &&
          <RecapGuarantyFormComponent modifyUrl={APP_URLS.guarantyPage}/>
        }
       
        <RecapBeneficiaryFormComponent  modifyUrl={APP_URLS.infoBeneficiaryStep2Page} />
       
        <div className='m-y-lg m-lg-desktop'>
          <MgpButton  
            type="button" 
            text="Mes informations personnelles" 
            color='bleu' 
            size="small" 
            variant = "inverse" 
            icon={isShowRecapInfoPerso === false ? "chevron-bottom" : "chevron-top"} 
            iconPosition ='right' 
            onClick={(e) => onShowRecapInfoPerso(e)}
          />
        </div>
        
        {isShowRecapInfoPerso &&
          <div className='m-lg-desktop'>
            <InfoPersoRecapFormComponent />
          </div>  
        }
        
        <button onClick={handleButtonClick}>
          {
            !isScrolledToBottom ? 
            <i className="icon-chevron-bottom scroll-button"></i> 
            :
            <i className="icon-chevron-top scroll-button"></i>
          }
        </button>
        
        {isPropositionInDraft(proposition!) ? ( <StickButtonsBloc disabledPreviousButton={false} showNextButton={true} showPreviousButton={true} previousAction={onPreviousAction} nextAction={showConfirmationModal} nextButtonName="Confirmer"/>) 
        : ( <StickButtonsBloc showNextButton={true} showPreviousButton={isConseiller} previousAction={onPreviousAction} nextAction={onNextLockedAction} />)}
      
        {loading && <LoaderModal hasMessage={true}/>}
        <ConfirmModal isOpen={isShowModal} onConfirmAction={onNextAction} onCloseModal={closeModal} text={<p className="ta-center bleu fz-lg fw-bold">Nous vous invitons à vérifier l'exactitude des informations saisies. Passé cette étape, il ne sera plus possible de les modifier.</p>} />
      </div>
    )
}